import { useState, useRef, useEffect } from "react";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import "./aws-config";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./App.css";

interface Source {
  go_number: string;
  department: string;
  date: string;
  page: number;
  subject?: string;
  doc_id?: number;
}

interface Msg {
  role: "user" | "assistant";
  text: string;
  sources?: Source[];
  log_id?: number;
  rating?: "up" | "down";
}

function extractFollowUps(text: string): { cleanText: string; questions: string[] } {
  const idx = text.search(/\*{0,2}Suggested follow-up questions:\*{0,2}/i);
  if (idx === -1) return { cleanText: text, questions: [] };
  const before = text.slice(0, idx).trimEnd();
  const after = text.slice(idx);
  const questions: string[] = [];
  const re = /^\d+\.\s+(.+)$/gm;
  let m;
  while ((m = re.exec(after)) !== null) {
    questions.push(m[1].replace(/\*\*/g, "").trim());
  }
  return { cleanText: before, questions: questions.slice(0, 3) };
}

function ChatApp({ signOut, user }: { signOut: () => void; user: any }) {
  const storageKey = `ap_go_chat_${user?.signInDetails?.loginId ?? "guest"}`;

  const [msgs, setMsgs] = useState<Msg[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as Msg[];
        // Drop any incomplete streaming messages from a previous interrupted session
        return parsed.filter(m => m.role === "user" || m.text.length > 0);
      }
    } catch { }
    return [];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastGoNumbers, setLastGoNumbers] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey + "_gos");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [depth, setDepth] = useState<"quick" | "focused" | "deep">("quick");
  const [model, setModel] = useState("us.anthropic.claude-sonnet-4-6");
  const [retrievalMeta, setRetrievalMeta] = useState<any>(null);
  const btm = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(msgs)); } catch { }
  }, [msgs, storageKey]);

  useEffect(() => {
    try { localStorage.setItem(storageKey + "_gos", JSON.stringify(lastGoNumbers)); } catch { }
  }, [lastGoNumbers, storageKey]);

  useEffect(() => {
    btm.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  async function ask(forcedQ?: string) {
    const q = forcedQ !== undefined ? forcedQ : input;
    if (!q.trim() || busy) return;
    if (forcedQ === undefined) setInput("");
    setBusy(true);
    setRetrievalMeta(null);
    const history = msgs.slice(-20).map(m => ({
      role: m.role,
      content: m.text,
      go_numbers: m.sources?.map((s: Source) => s.go_number).filter(Boolean) || []
    }));
    setMsgs(p => [...p,
    { role: "user", text: q },
    { role: "assistant", text: "", sources: [] }
    ]);
    try {
      const res = await fetch("https://go.t-factorial.com/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, history, last_go_numbers: lastGoNumbers, depth, model, user_email: user?.signInDetails?.loginId ?? "" })
      });
      const reader = res.body!.getReader();
      const dec = new TextDecoder();
      let srcs: Source[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of dec.decode(value).split("\n").filter(Boolean)) {
          if (!line.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === "sources") {
              srcs = d.sources;
              // Track GO numbers from this response for follow-up context
              const goNums = d.sources.map((s: Source) => s.go_number).filter(Boolean);
              if (goNums.length > 0) setLastGoNumbers(goNums);
            }
            if (d.type === "sources_final") {
              // POST-GENERATION CORRECTION: replace preliminary sources with only
              // the GOs Claude actually cited in its answer
              srcs = d.sources;
              setMsgs(p => {
                const u = [...p];
                u[u.length - 1] = { ...u[u.length - 1], sources: d.sources };
                return u;
              });
              const goNums = d.sources.map((s: Source) => s.go_number).filter(Boolean);
              setLastGoNumbers(goNums.length > 0 ? goNums : lastGoNumbers);
            }
            if (d.type === "retrieval_meta") {
              setRetrievalMeta(d);
            }
            if (d.type === "text") {
              setMsgs(p => {
                const u = [...p];
                u[u.length - 1] = {
                  ...u[u.length - 1],
                  text: u[u.length - 1].text + d.text,
                  sources: srcs
                };
                return u;
              });
            }
            if (d.type === "_log_id") {
              setMsgs(p => {
                const u = [...p];
                u[u.length - 1] = { ...u[u.length - 1], log_id: d.log_id };
                return u;
              });
            }
          } catch { }
        }
      }
    } catch {
      setMsgs(p => {
        const u = [...p];
        u[u.length - 1] = {
          ...u[u.length - 1],
          text: "Unable to get a response. Please try again in a moment."
        };
        return u;
      });
    }
    setBusy(false);
  }

  async function sendFeedback(msgIndex: number, rating: "up" | "down") {
    setMsgs(p => {
      const u = [...p];
      u[msgIndex] = { ...u[msgIndex], rating };
      return u;
    });
    try {
      await fetch("https://go.t-factorial.com/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          log_id: msgs[msgIndex]?.log_id,
          rating
        })
      });
    } catch { }
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100vh", fontFamily: "system-ui",
      background: "#f0f4f8"
    }}>
      {/* ── Header ── */}
      <div style={{
        background: "linear-gradient(135deg, #1a3a5c 0%, #2a5298 100%)",
        color: "white",
        padding: "18px 32px",
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        boxShadow: "0 3px 16px rgba(0,0,0,0.25)",
        flexShrink: 0
      }}>
        {/* Left — empty spacer */}
        <div />

        {/* Center — branding */}
        <div style={{ display: "flex", alignItems: "center", gap: "14px", justifyContent: "center" }}>
          <div style={{ fontSize: "34px", lineHeight: 1 }}>🏛️</div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "21px", fontWeight: 700, letterSpacing: "-0.3px", lineHeight: 1.2 }}>
              AP Government Orders
            </div>
            <div style={{ fontSize: "16px", opacity: 0.7, marginTop: "3px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Knowledge Base & Powered with AI
            </div>
          </div>
        </div>

        {/* Right — user, new chat & sign out */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "flex-end" }}>
          <div style={{ fontSize: "12px", opacity: 0.8 }}>
            {user?.signInDetails?.loginId}
          </div>
          <button onClick={() => { setMsgs([]); localStorage.removeItem(storageKey); }} style={{
            padding: "8px 18px",
            background: "rgba(255,255,255,0.1)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: "8px", fontSize: "13px",
            cursor: "pointer", fontWeight: 500
          }}>
            New chat
          </button>
          <button onClick={signOut} style={{
            padding: "8px 18px",
            background: "rgba(255,255,255,0.15)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.35)",
            borderRadius: "8px", fontSize: "13px",
            cursor: "pointer", fontWeight: 500
          }}>
            Sign out
          </button>
        </div>
      </div>

      {/* ── Centered chat column ── */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        width: "100%", maxWidth: "900px",
        margin: "0 auto", minHeight: 0
      }}>
        {/* Messages */}
        <div style={{
          flex: 1, overflow: "auto", padding: "30px 24px 16px",
          display: "flex", flexDirection: "column", gap: "16px"
        }}>
          {msgs.length === 0 && (
            <div style={{ textAlign: "center", marginTop: "80px" }}>
              <div style={{ fontSize: "52px", marginBottom: "16px" }}>🏛️</div>
              <div style={{
                fontSize: "24px", fontWeight: 700,
                color: "#1a3a5c", marginBottom: "8px"
              }}>
                Welcome {user?.signInDetails?.loginId?.split("@")[0]}
              </div>
              <div style={{ fontSize: "15px", color: "#778899", marginBottom: "30px" }}>
                Ask anything about AP Government Orders
              </div>
              <div style={{
                display: "flex", gap: "10px",
                justifyContent: "center", flexWrap: "wrap"
              }}>
                {[
                  "What does the Agriculture GO say?",
                  "Show me Law department orders",
                  "What are the GAD orders about?",
                  "Summarise School Education rules"
                ].map((q, i) => (
                  <button key={i} onClick={() => setInput(q)} style={{
                    background: "white", border: "1px solid #c8d8eb",
                    borderRadius: "24px", padding: "10px 20px",
                    fontSize: "13px", cursor: "pointer", color: "#1a3a5c",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.07)", fontWeight: 500
                  }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => {
            const { cleanText, questions } = m.role === "assistant"
              ? extractFollowUps(m.text)
              : { cleanText: m.text, questions: [] as string[] };
            const streaming = busy && i === msgs.length - 1;
            return (
              <div key={i} style={{
                maxWidth: "78%",
                alignSelf: m.role === "user" ? "flex-end" : "flex-start"
              }}>
                {m.role === "assistant" && i === msgs.length - 1 && retrievalMeta && (
                  <div style={{
                    fontSize: 12,
                    color: "#888",
                    padding: "4px 12px",
                    borderBottom: "1px solid #eee",
                    marginBottom: 8
                  }}>
                    Found {retrievalMeta.chunks_found} chunks from {retrievalMeta.gos_found} GOs
                    {retrievalMeta.filters_relaxed && " (filters broadened)"}
                    {retrievalMeta.query_expanded && " (query expanded)"}
                    {retrievalMeta.quality && ` • Quality: ${retrievalMeta.quality}`}
                  </div>
                )}
                <div style={{
                  background: m.role === "user" ? "#1a3a5c" : "white",
                  color: m.role === "user" ? "white" : "#111",
                  padding: "14px 18px", borderRadius: "14px",
                  border: m.role === "assistant" ? "1px solid #dde6f0" : "none",
                  fontSize: "14px", lineHeight: "1.7",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.07)"
                }}>
                  {m.role === "user" ? (
                    <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: (p: any) => <h1 style={{ fontSize: "18px", fontWeight: 700, margin: "10px 0 6px", color: "#1a3a5c", borderBottom: "1px solid #dde6f0", paddingBottom: "4px" }}>{p.children}</h1>,
                        h2: (p: any) => <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "10px 0 5px", color: "#1a3a5c" }}>{p.children}</h2>,
                        h3: (p: any) => <h3 style={{ fontSize: "14px", fontWeight: 700, margin: "8px 0 4px", color: "#1a3a5c" }}>{p.children}</h3>,
                        p: (p: any) => <p style={{ margin: "5px 0", lineHeight: "1.7" }}>{p.children}</p>,
                        ul: (p: any) => <ul style={{ margin: "6px 0", paddingLeft: "22px" }}>{p.children}</ul>,
                        ol: (p: any) => <ol style={{ margin: "6px 0", paddingLeft: "22px" }}>{p.children}</ol>,
                        li: (p: any) => <li style={{ margin: "3px 0", lineHeight: "1.6" }}>{p.children}</li>,
                        strong: (p: any) => <strong style={{ fontWeight: 700, color: "#1a3a5c" }}>{p.children}</strong>,
                        table: (p: any) => <div style={{ overflowX: "auto", margin: "10px 0", borderRadius: "8px", border: "1.5px solid #b0c4de" }}><table style={{ borderCollapse: "collapse", minWidth: "100%", fontSize: "13px", whiteSpace: "nowrap" }}>{p.children}</table></div>,
                        thead: (p: any) => <thead>{p.children}</thead>,
                        th: (p: any) => <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 700, color: "white", background: "#1a3a5c", whiteSpace: "nowrap", borderRight: "1px solid rgba(255,255,255,0.25)" }}>{p.children}</th>,
                        td: (p: any) => <td style={{ padding: "8px 14px", borderBottom: "1px solid #b8ccdf", borderRight: "1px solid #c8d8ea", verticalAlign: "top", color: "#111111", fontWeight: 400, whiteSpace: "nowrap" }}>{p.children}</td>,
                        tr: (p: any) => <tr style={{ background: "#e8f2ff" }} onMouseEnter={e => (e.currentTarget.style.background = "#c8dff8")} onMouseLeave={e => (e.currentTarget.style.background = "#e8f2ff")}>{p.children}</tr>,
                        code: (p: any) => p.inline
                          ? <code style={{ background: "#f0f4f8", padding: "2px 6px", borderRadius: "4px", fontSize: "12px", fontFamily: "monospace", color: "#1a3a5c" }}>{p.children}</code>
                          : <pre style={{ background: "#f0f4f8", padding: "12px 16px", borderRadius: "8px", overflowX: "auto", margin: "8px 0" }}><code style={{ fontSize: "12px", fontFamily: "monospace" }}>{p.children}</code></pre>,
                        blockquote: (p: any) => <blockquote style={{ borderLeft: "3px solid #c8d8eb", margin: "8px 0", paddingLeft: "14px", color: "#556677", fontStyle: "italic" }}>{p.children}</blockquote>,
                      }}
                    >
                      {cleanText || (streaming ? "Searching Government Orders…" : "")}
                    </ReactMarkdown>
                  )}
                  {m.role === "assistant" && m.text.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                      <button
                        onClick={() => sendFeedback(i, "up")}
                        className={`feedback-btn ${m.rating === "up" ? "selected-up" : ""}`}
                        title="Thumb Up"
                      >👍</button>
                      <button
                        onClick={() => sendFeedback(i, "down")}
                        className={`feedback-btn ${m.rating === "down" ? "selected-down" : ""}`}
                        title="Thumb Down"
                      >👎</button>
                    </div>
                  )}
                </div>
                {(m.sources || []).length > 0 && (
                  <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {m.sources!.map((s, j) => (
                      <div key={j}
                        onClick={() => {
                          // Use doc_id in the PATH — CloudFront strips query params
                          // but cannot strip path segments
                          if (s.doc_id) {
                            window.open(`https://go.t-factorial.com/api/download-by-id/${s.doc_id}`, "_blank");
                          } else {
                            window.open(`https://go.t-factorial.com/api/download/${encodeURIComponent(s.go_number)}`, "_blank");
                          }
                        }}
                        style={{
                          background: "#EBF3FF", border: "1px solid #B8D4F5",
                          borderRadius: "8px", padding: "6px 12px",
                          fontSize: "11px", color: "#1a3a5c", cursor: "pointer"
                        }}
                        title="Click to download PDF"
                      >
                        <div style={{ fontWeight: 700 }}>⬇ {s.go_number}</div>
                        <div style={{ opacity: 0.8 }}>{s.department} · {s.date} · Page {s.page}</div>
                        {s.subject && (
                          <div style={{
                            opacity: 0.65, fontSize: "10px", marginTop: "2px",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px"
                          }}>
                            {s.subject}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {questions.length > 0 && !streaming && (
                  <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {questions.map((q, qi) => (
                      <button key={qi} onClick={() => ask(q)} style={{
                        background: "white", border: "1.5px solid #2a5298",
                        borderRadius: "20px", padding: "7px 14px",
                        fontSize: "12px", color: "#2a5298", cursor: "pointer",
                        fontWeight: 500, textAlign: "left", maxWidth: "300px"
                      }}>
                        ↗ {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={btm} />
        </div>

        {/* Input bar */}
        <div style={{
          padding: "16px 24px 22px", background: "white",
          borderTop: "1px solid #dde6f0",
          display: "flex", gap: "12px", alignItems: "center",
          boxShadow: "0 -2px 12px rgba(0,0,0,0.06)"
        }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && ask()}
            placeholder="Ask a question about AP Government Orders…"
            style={{
              flex: 1, padding: "14px 18px",
              border: "1.5px solid #c8d8eb", borderRadius: "12px",
              fontSize: "15px", outline: "none", background: "#f8fafc"
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ fontSize: "10px", color: "#778899", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", paddingLeft: "2px" }}>
              Depth
            </div>
            <select
              value={depth}
              onChange={e => setDepth(e.target.value as any)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #ddd",
                fontSize: 13,
                backgroundColor: "white",
                cursor: "pointer",
                minWidth: "130px"
              }}
            >
              <option value="quick">⚡ Quick Research</option>
              <option value="focused">🔍 Focused Research</option>
              <option value="deep">🔬 Deep Research</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ fontSize: "10px", color: "#778899", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", paddingLeft: "2px" }}>
              Model
            </div>
            <select
              value={model}
              onChange={e => setModel(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #ddd",
                fontSize: 13,
                backgroundColor: "white",
                cursor: "pointer",
                minWidth: "160px"
              }}
            >
              <option value="us.anthropic.claude-sonnet-4-6">Claude Sonnet 4.6</option>
              <option value="us.anthropic.claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
            </select>
          </div>
          <button onClick={() => ask()} disabled={busy} style={{
            padding: "14px 32px",
            background: busy ? "#aab8c8" : "#1a3a5c",
            color: "white", border: "none",
            borderRadius: "12px", fontSize: "15px",
            cursor: busy ? "not-allowed" : "pointer",
            fontWeight: 700
          }}>
            {busy ? "…" : "Ask"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Authenticator
      hideSignUp={true}
      components={{
        Header() {
          return (
            <div style={{
              textAlign: "center",
              padding: "24px 20px 0",
              background: "#1a3a5c"
            }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>🏛️</div>
              <div style={{
                fontSize: "18px", fontWeight: 600,
                color: "white", marginBottom: "4px"
              }}>
                AP Government Orders
              </div>
              <div style={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.7)",
                paddingBottom: "20px"
              }}>
                Authorised access only
              </div>
            </div>
          );
        }
      }}
    >
      {({ signOut, user }) => (
        <ChatApp signOut={signOut!} user={user} />
      )}
    </Authenticator>
  );
}
